import { useState } from 'react';
import { Select, InputNumber, Input } from 'antd';
import { Space, Button } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';
import cx from 'classnames';

import { JsonDataItem, type JsonData, type WithOptionalDataTest } from 'types';
import styles from './parts-list.module.scss';

export enum Providers {
  G2U = 'technician',
  OW = 'office',
  New = 'new',
}

type Props = {
  selectedPart: JsonDataItem;
  isAttachedToJob: boolean;
  partsList: JsonData;
  handlePriceChange: (productId: number, value: string) => void;
  handleQtyChange: (productId: number, value: number) => void;
  handleProductChange: (productId: number | string, value: JsonDataItem | null) => void;
  handleRemoveProduct: (productId: number) => void;
} & WithOptionalDataTest;

export const PartsItem: React.FC<Props> = observer((props) => {
  const {
    selectedPart,
    isAttachedToJob,
    partsList,
    handleProductChange,
    handlePriceChange,
    handleQtyChange,
    handleRemoveProduct,
  } = props;
  const [customPrice, setCustomPrice] = useState<string>('0.00');
  const [selectedPartType, setSelectedPartType] = useState<Providers>(
    selectedPart?.type || (selectedPart.id?.toString().includes('temp') ? Providers.New : Providers.OW),
  );

  return (
    <div className={styles.partsList__item} key={selectedPart.id}>
      <Space.$ compact className={styles.partsList__itemField}>
        <Select
          defaultValue={selectedPartType}
          className={styles.partsList__itemFieldPreffix}
          disabled={isAttachedToJob}
          onChange={(val) => {
            setSelectedPartType(val);
            if (val === Providers.New) {
              handleProductChange(`temp_${new Date().getTime()}`, null);
            } else {
              handleProductChange(selectedPart.id, null);
            }
          }}
          style={{ minWidth: 160 }}
        >
          <Select.Option value={Providers.OW}>Geeks2U Supplied</Select.Option>
          <Select.Option value={Providers.G2U}>Tech Inventory</Select.Option>
          <Select.Option value={Providers.New}>New Part</Select.Option>
        </Select>

        {selectedPartType === Providers.New ? (
          <Input
            placeholder="Part name"
            className={cx(styles.partsList__itemFieldInput, styles.partsList__itemFieldInput_wide)}
            onChange={(e) => {
              handleProductChange(selectedPart.id, { description: e.target.value, total_retail: customPrice });
            }}
          />
        ) : (
          <Select
            value={selectedPart.description && selectedPart.id}
            onChange={(productId: number) => {
              handleProductChange(productId, partsList.find((item) => item.ID === productId) || null);
            }}
            style={{ width: '100%', marginRight: 8 }}
            placeholder="Select"
            optionFilterProp="children"
            className={cx(styles.partsList__itemFieldSelect)}
            showSearch
            disabled={isAttachedToJob}
          >
            {isAttachedToJob ? (
              <Select.Option value={selectedPart.id}>{selectedPart.description}</Select.Option>
            ) : (
              partsList
                .filter((part: JsonDataItem) => part.type === selectedPartType)
                ?.map((option: JsonDataItem) => (
                  <Select.Option key={option.ID} value={option.ID}>
                    {option.description}
                  </Select.Option>
                ))
            )}
          </Select>
        )}
      </Space.$>
      <>
        {(selectedPart?.id || selectedPartType === Providers.New) && (
          <>
            <Input
              addonBefore={'$'}
              placeholder="0.00"
              className={cx(
                styles.partsList__itemFieldInput,
                selectedPartType !== Providers.New && styles.partsList__itemFieldInput_disabled,
              )}
              disabled={selectedPartType !== Providers.New}
              onChange={(e) => {
                if (/^[\d.]*$/.test(e.target.value)) {
                  setCustomPrice(e.target.value);
                  handlePriceChange(selectedPart.id, e.target.value);
                }
              }}
              value={selectedPart.price}
            />
            <InputNumber
              value={selectedPart.qty || selectedPart.qty}
              formatter={(value) => (value ? String(value).replace(/[^0-9.]/g, '') : '')}
              parser={(value) => (value ? value.replace(/[^0-9.]/g, '') : '')}
              onChange={(value) => {
                if (/^[\d.]*$/.test(value) && !!value) {
                  handleQtyChange(selectedPart.id, value as number);
                }
              }}
              min={1}
              max={selectedPart.details?.qty_available}
              width={55}
              className={cx(styles.partsList__qty, styles.partsList__qty_required)}
              disabled={isAttachedToJob}
            />
          </>
        )}
        <div className={styles.partsList__price}>
          <Button.$
            dataTestId="remove-product-1"
            size={Button.Size.Small}
            type={Button.Type.Link}
            className={styles.partsList__remove}
            onClick={() => {
              handleRemoveProduct(selectedPart.id);
            }}
          >
            Remove
          </Button.$>
        </div>
      </>
    </div>
  );
});
